import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api, fehlertext } from '../api'
import { useAktion, useLaden } from '../hooks'
import Meldung from '../komponenten/Meldung'
import Meldungen from '../komponenten/Meldungen'
import { datum, euro, menge, preis, stueck, tonnen, zahlLesen, zeitpunkt } from '../format'
import { VORSCHLAGSTATUS, staffelSchwelle } from '../texte'
import type { Bestellvorschlag, Lieferant, Staffel, Variante, VarianteGruppe, VorschlagPosition } from '../typen'

/**
 * Der Zettel für den Chef: für einen Lieferanten mehrere Varianten, wie ein Abruf oder eine
 * Nachauflage aussähe - je Artikel Paletten, Stück, Tonnen, Reichweite vorher und nachher, Preis
 * und Kosten. Eine Variante wird gewählt; erst das erzeugt Bestellungen. Der Vorschlag bleibt als
 * Begründung stehen und lässt sich drucken oder als Excel mitnehmen.
 *
 * Seit 02.11.2026: hat der Lieferant Artikelgruppen, lässt sich der Vorschlag auf eine Gruppe beschränken. Im
 * Entwurf sind die Paletten je Position änderbar („von 30 Paletten 10 davon und 20 davon“, Göran); „Neu rechnen“
 * schickt sie ans Backend, das die Variante mit festen Paletten neu rechnet. Darüber steht je Artikelgruppe die
 * Summe, ob der Mindestabruf erreicht ist, die erreichte und die nächste Staffel und die Kosten.
 */

const ARTTEXT = { abruf: 'Abruf', nachauflage: 'Nachauflage' } as const

export default function BestellenSeite() {
  const [suchparameter] = useSearchParams()
  const [lieferanten, setLieferanten] = useState<Lieferant[]>([])
  const [lieferant, setLieferant] = useState(suchparameter.get('lieferant') ?? '')
  const [art, setArt] = useState<'abruf' | 'nachauflage'>('nachauflage')
  const [ziel, setZiel] = useState(120)
  const [gruppe, setGruppe] = useState(suchparameter.get('gruppe') ?? '')
  // Artikelgruppe des Lieferanten (nicht das Blatt): nur ihre Artikel rechnen
  const [artikelgruppe, setArtikelgruppe] = useState(suchparameter.get('artikelgruppe') ?? '')
  const [gruppen, setGruppen] = useState<string[]>([])
  const [vorschlag, setVorschlag] = useState<Bestellvorschlag | null>(null)
  const [bemerkung, setBemerkung] = useState('')
  const { fehler, erfolg, arbeitet, setFehler, setErfolg, ausfuehren } = useAktion()
  const { daten: geladeneAlte, fehler: alteFehler, neuLaden: alteLaden } = useLaden<Bestellvorschlag[]>(() => api.bestellvorschlaege(), [])
  const alte = geladeneAlte ?? []
  const [laedtNoch, setLaedtNoch] = useState(true)
  const [empfiehlt, setEmpfiehlt] = useState(false)

  /** Eigener Beschäftigt-Schalter: während die KI abwägt, bleiben Rechnen und Entscheiden bedienbar. */
  async function empfehlung() {
    if (!vorschlag) return
    setEmpfiehlt(true)
    setFehler(null)
    try {
      setVorschlag(await api.bestellvorschlagEmpfehlung(vorschlag.id))
    } catch (f) {
      setFehler(fehlertext(f))
    } finally {
      setEmpfiehlt(false)
    }
  }

  // Bleibt von Hand: der erste Lieferant wird vorbelegt, und „lädt noch“ entscheidet über die Leer-Seite.
  useEffect(() => {
    api
      .lieferanten()
      .then((werte) => {
        setLieferanten(werte)
        if (!lieferant && werte.length > 0) setLieferant(werte[0].name)
      })
      .catch((f) => setFehler(fehlertext(f)))
      .finally(() => setLaedtNoch(false))
    api.gruppen().then(setGruppen).catch(() => setGruppen([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function rechnen() {
    if (!lieferant) {
      setFehler('Lieferant wählen.')
      return
    }
    void ausfuehren(async () => {
      setVorschlag(
        await api.bestellvorschlagRechnen({
          lieferant,
          art,
          zielreichweite_tage: ziel,
          gruppe: gruppe || null,
          // nur mitschicken, wenn gewählt - ein älteres Backend kennt das Feld nicht
          ...(artikelgruppe && artikelgruppenZurWahl.some((g) => g.name === artikelgruppe) ? { artikelgruppe } : {}),
        }),
      )
      setBemerkung('')
      alteLaden()
    })
  }

  function entscheiden(index: number) {
    if (!vorschlag) return
    void ausfuehren(async () => {
      const neu = await api.bestellvorschlagEntscheiden(vorschlag.id, index, bemerkung || undefined)
      setVorschlag(neu)
      setErfolg(
        `Variante ${index + 1} entschieden. ${neu.varianten[index].positionen.filter((p) => p.menge_t > 0).length} Bestellung(en) angelegt - sie stehen jetzt in der Disposition${
          neu.art === 'abruf' ? ' als beauftragt' : ' als in Produktion'
        }.`,
      )
      alteLaden()
    })
  }

  /** Paletten je Position von Hand: das Backend rechnet die Variante mit festen Paletten neu. */
  function aufteilen(index: number, paletten: Record<string, number>) {
    if (!vorschlag) return
    void ausfuehren(async () => {
      setVorschlag(await api.varianteAufteilen(vorschlag.id, index, paletten))
      alteLaden()
    }, `Variante ${index + 1} mit ${Object.keys(paletten).length} geänderten Position(en) neu gerechnet.`)
  }

  function verwerfen() {
    if (!vorschlag) return
    if (!window.confirm(`Vorschlag ${ARTTEXT[vorschlag.art]} ${vorschlag.lieferant} verwerfen? Er bleibt in der Liste, lässt sich aber nicht mehr entscheiden.`)) return
    void ausfuehren(async () => {
      setVorschlag(await api.bestellvorschlagVerwerfen(vorschlag.id))
      alteLaden()
    })
  }

  const gewaehlterLieferant = lieferanten.find((l) => l.name === lieferant)
  const artikelgruppenZurWahl = gewaehlterLieferant?.gruppen ?? []
  // Der Lieferant des angezeigten Vorschlags - für die Gruppensummen; kann ein anderer sein als der gewählte.
  const vorschlagLieferant = vorschlag ? lieferanten.find((l) => l.id === vorschlag.lieferant_id) : undefined
  // Ohne Preisstaffeln bleiben Preis und Kosten in jeder Zeile leer - dann fallen die Spalten weg,
  // und der Grund steht einmal da (im Erklärtext oben, wenn er denselben Lieferanten meint).
  const ohnePreise =
    vorschlag !== null && vorschlag.varianten.every((v) => v.positionen.every((p) => p.preis_je_1000 === null && p.kosten === null))
  const preisHinweisObenSchonDa =
    gewaehlterLieferant !== undefined && vorschlag !== null && gewaehlterLieferant.name === vorschlag.lieferant && gewaehlterLieferant.staffeln.length === 0

  if (!laedtNoch && lieferanten.length === 0) {
    return (
      <>
        <h2>Bestellen</h2>
        <p className="leer">
          Es ist noch kein Lieferant angelegt. Der Bestellvorschlag rechnet über das Sortiment eines Lieferanten
          (Mindestabruf, Staffeln, Paarigkeit) - dafür braucht er Vorlauf, Puffer und Produktionszeit.
          <br />
          <br />
          <Link to="/lieferanten">Lieferanten anlegen</Link> oder auf der Disposition unter „Je Lieferant" mit einem
          Klick aus dem Hersteller machen. Schneller geht es im <Link to="/assistent">Assistenten</Link>: „Lege DS Smith
          als Lieferant an, 30 Paletten Mindestabruf, 11 Wochen Produktionszeit."
        </p>
      </>
    )
  }

  return (
    <>
      <div className="leiste nicht-drucken">
        <h2>Bestellen</h2>
        <select
          value={lieferant}
          onChange={(e) => {
            setLieferant(e.target.value)
            setArtikelgruppe('')
          }}
        >
          {lieferanten.length === 0 && <option value="">kein Lieferant angelegt</option>}
          {lieferanten.map((l) => (
            <option key={l.id} value={l.name}>
              {l.name}
              {l.abruf_mengenregel_text ? ` (Abruf ${l.abruf_mengenregel_text})` : ''}
            </option>
          ))}
        </select>
        <select value={art} onChange={(e) => setArt(e.target.value as 'abruf' | 'nachauflage')}>
          <option value="nachauflage">Nachauflage (produzieren lassen)</option>
          <option value="abruf">Abruf (vom Hersteller holen)</option>
        </select>
        <label>
          Reichweite{' '}
          <select value={ziel} onChange={(e) => setZiel(Number(e.target.value))}>
            <option value={42}>6 Wochen</option>
            <option value={84}>12 Wochen</option>
            <option value={120}>4 Monate</option>
            <option value={180}>6 Monate</option>
            <option value={270}>9 Monate</option>
            <option value={365}>1 Jahr</option>
          </select>
        </label>
        <select value={gruppe} onChange={(e) => setGruppe(e.target.value)}>
          <option value="">alle Blätter</option>
          {gruppen.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
        {artikelgruppenZurWahl.length > 0 && (
          <select
            value={artikelgruppe}
            title="Nur die Artikel dieser Artikelgruppe des Lieferanten rechnen"
            onChange={(e) => setArtikelgruppe(e.target.value)}
          >
            <option value="">alle Artikelgruppen</option>
            {artikelgruppenZurWahl.map((g) => (
              <option key={g.id} value={g.name}>
                Gruppe {g.name} ({g.artikel.length} Artikel)
              </option>
            ))}
          </select>
        )}
        <button className="haupt" disabled={arbeitet || !lieferant} onClick={rechnen}>
          Vorschlag rechnen
        </button>
      </div>

      {gewaehlterLieferant && (
        <p className="hinweis nicht-drucken" style={{ maxWidth: '80ch' }}>
          {gewaehlterLieferant.name}: {gewaehlterLieferant.artikel.length} Artikel
          {artikelgruppenZurWahl.length > 0
            ? ` in ${artikelgruppenZurWahl.length} ${artikelgruppenZurWahl.length === 1 ? 'Artikelgruppe' : 'Artikelgruppen'}`
            : ''}
          {gewaehlterLieferant.mindestabruf_paletten ? `, Mindestabruf ${gewaehlterLieferant.mindestabruf_paletten} Paletten` : ', kein Mindestabruf hinterlegt'}
          {gewaehlterLieferant.staffeln.length > 0
            ? `, Staffeln ${gewaehlterLieferant.staffeln
                .map((s) => `${staffelSchwelle(s)}${s.artikelgruppe ? ` (${s.artikelgruppe})` : ''}`)
                .join(' / ')}`
            : ', keine Preisstaffeln hinterlegt'}
          . Reichweite und Bedarf kommen aus dem Dispositionsblatt: Ist-Verbrauch gegen Forecast, das frühere Ende zählt.
        </p>
      )}

      <Meldungen fehler={alteFehler ?? fehler} erfolg={erfolg} />

      {vorschlag && (
        <section className="druckbogen">
          <div className="leiste">
            <h3>
              {ARTTEXT[vorschlag.art]} {vorschlag.lieferant} · Stand {datum(vorschlag.stichtag)} · Ziel {vorschlag.zielreichweite_tage} Tage
            </h3>
            <span className={`status ${VORSCHLAGSTATUS[vorschlag.status].klasse}`}>
              {VORSCHLAGSTATUS[vorschlag.status].text}
            </span>
            <a className="knopf nicht-drucken" href={api.exportBestellvorschlag(vorschlag.id)} download>
              Als Excel
            </a>
            <button className="nicht-drucken" onClick={() => window.print()}>
              Drucken
            </button>
            {vorschlag.status === 'entwurf' && (
              <button className="nicht-drucken" disabled={arbeitet || empfiehlt} onClick={() => void empfehlung()}>
                {empfiehlt ? 'KI wägt ab (bis zu drei Minuten)…' : vorschlag.empfehlung ? 'Empfehlung erneuern' : 'Empfehlung für den Chef'}
              </button>
            )}
            {vorschlag.status === 'entwurf' && (
              <button className="nicht-drucken" disabled={arbeitet} onClick={verwerfen}>
                Vorschlag verwerfen
              </button>
            )}
          </div>
          {ohnePreise && !preisHinweisObenSchonDa && (
            <p className="hinweis">Keine Preisstaffeln hinterlegt - Preis und Kosten bleiben leer.</p>
          )}

          {vorschlag.empfehlung && (
            <div className="karte empfehlung">
              <h4 style={{ margin: '0 0 6px' }}>
                Empfehlung: Variante {vorschlag.empfehlung.variante + 1}
                {vorschlag.varianten[vorschlag.empfehlung.variante] ? ` - ${vorschlag.varianten[vorschlag.empfehlung.variante].titel}` : ''}
              </h4>
              <div className="empfehlung-bloecke">
                <div className="empfehlung-block">
                  <h5>Begründung</h5>
                  <Absaetze text={vorschlag.empfehlung.begruendung} />
                </div>
                {vorschlag.empfehlung.risiken.length > 0 && (
                  <div className="empfehlung-block">
                    <h5>Risiken</h5>
                    <ul>
                      {vorschlag.empfehlung.risiken.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {vorschlag.empfehlung.fragen.length > 0 && (
                  <div className="empfehlung-block">
                    <h5>Vorher klären</h5>
                    <ul>
                      {vorschlag.empfehlung.fragen.map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <details className="nicht-drucken">
                <summary>Mail-Entwurf an {vorschlag.lieferant}: {vorschlag.empfehlung.mail_betreff}</summary>
                <pre className="mail-entwurf">{vorschlag.empfehlung.mail_text}</pre>
                <button
                  onClick={() => {
                    const e = vorschlag.empfehlung
                    if (!e) return
                    void navigator.clipboard.writeText(`Betreff: ${e.mail_betreff}\n\n${e.mail_text}`).then(() => setErfolg('Mail-Entwurf kopiert.'))
                  }}
                >
                  Kopieren
                </button>
              </details>
              <p className="hinweis" style={{ margin: '6px 0 0' }}>
                Vorschlag der KI aus Varianten, Bestellregeln und Disposition - die Entscheidung bleibt beim Chef.
              </p>
            </div>
          )}

          {vorschlag.varianten.map((v, index) => (
            <VarianteKarte
              key={index}
              variante={v}
              nummer={index + 1}
              gewaehlt={vorschlag.gewaehlt === index}
              entscheidbar={vorschlag.status === 'entwurf' && !arbeitet}
              aufteilbar={vorschlag.status === 'entwurf'}
              gesperrt={arbeitet}
              preiseZeigen={!ohnePreise}
              lieferant={vorschlagLieferant}
              onWaehlen={() => entscheiden(index)}
              onAufteilen={(paletten) => aufteilen(index, paletten)}
              onFehler={setFehler}
            />
          ))}

          {vorschlag.status === 'entwurf' && (
            <div className="leiste nicht-drucken" style={{ marginTop: 10 }}>
              <input
                type="text"
                style={{ flex: 1, minWidth: 260 }}
                placeholder="Entscheidung des Chefs in Worten (optional)"
                value={bemerkung}
                onChange={(e) => setBemerkung(e.target.value)}
              />
              <span className="hinweis">wird mit „Diese Variante bestellen" gespeichert</span>
            </div>
          )}
          {vorschlag.status !== 'entwurf' && (
            <p className="hinweis">
              {VORSCHLAGSTATUS[vorschlag.status].text} am {vorschlag.entschieden_am ? zeitpunkt(vorschlag.entschieden_am) : ''}
              {vorschlag.bemerkung ? ` - „${vorschlag.bemerkung}“` : ''}
            </p>
          )}
        </section>
      )}

      {alte.length > 0 && (
        <div className="karte nicht-drucken" style={{ marginTop: 18 }}>
          <h3>Bisherige Vorschläge</h3>
          <table>
            <thead>
              <tr>
                <th>Angelegt</th>
                <th>Lieferant</th>
                <th>Art</th>
                <th className="zahl">Ziel</th>
                <th>Status</th>
                <th>Entscheidung</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {alte.map((v) => (
                <tr key={v.id}>
                  <td>{zeitpunkt(v.angelegt_am)}</td>
                  <td>{v.lieferant}</td>
                  <td>{ARTTEXT[v.art]}</td>
                  <td className="zahl">{v.zielreichweite_tage} Tage</td>
                  <td>
                    <span className={`status ${VORSCHLAGSTATUS[v.status].klasse}`}>
                      {VORSCHLAGSTATUS[v.status].text}
                    </span>
                  </td>
                  <td className="hinweis">
                    {v.gewaehlt !== null ? `Variante ${v.gewaehlt + 1}` : ''}
                    {v.bemerkung ? ` - ${v.bemerkung}` : ''}
                  </td>
                  <td>
                    <button onClick={() => setVorschlag(v)}>Ansehen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

/** Ein Fließtext der KI in Absätzen - Leerzeilen trennen; ein Absatz ohne Leerzeile bleibt einer. */
function Absaetze({ text }: { text: string }) {
  const teile = text
    .split(/\n\s*\n|\n/)
    .map((t) => t.trim())
    .filter(Boolean)
  return (
    <>
      {teile.map((t, i) => (
        <p key={i}>{t}</p>
      ))}
    </>
  )
}

/** Paletten je Position als Text - das Feld hält Text, bis neu gerechnet wird. */
function palettenTexte(v: Variante): Record<string, string> {
  return Object.fromEntries(v.positionen.map((p) => [p.artikelnummer, String(p.paletten)]))
}

/** Eine Palettenzahl lesbar: ganze Zahl ohne Komma, sonst eine Stelle. */
function pal(wert: number): string {
  return Number.isInteger(wert) ? String(wert) : menge(wert)
}

/**
 * Welche Staffel eine Gruppe erreicht und welche als nächste kommt. Es gelten die Stufen der Gruppe; hat sie keine,
 * die ohne Gruppe - und die zählen über das ganze Sortiment der Variante. Jede Stufe zählt in ihrer Einheit.
 */
function staffelStand(
  staffeln: Staffel[],
  gruppe: string | null,
  inGruppe: { paletten: number; stueck: number },
  gesamt: { paletten: number; stueck: number },
): { erreicht: string | null; naechste: string | null } {
  const eigene = gruppe === null ? [] : staffeln.filter((s) => s.artikelgruppe === gruppe)
  const gelten = eigene.length > 0 ? eigene : staffeln.filter((s) => !s.artikelgruppe)
  const zaehlt = eigene.length > 0 ? inGruppe : gesamt
  if (gelten.length === 0) return { erreicht: null, naechste: null }
  const nachPaletten = (s: Staffel) => s.ab_paletten !== null && s.ab_paletten !== undefined
  const schwelle = (s: Staffel) => (nachPaletten(s) ? (s.ab_paletten ?? 0) : (s.ab_stueck ?? 0))
  const gezaehlt = (s: Staffel) => (nachPaletten(s) ? zaehlt.paletten : zaehlt.stueck)
  const beste = gelten.filter((s) => gezaehlt(s) >= schwelle(s)).sort((a, b) => a.preis_je_1000 - b.preis_je_1000)[0]
  // die nächstgünstigere Stufe, die noch fehlt
  const offen = gelten
    .filter((s) => gezaehlt(s) < schwelle(s) && (!beste || s.preis_je_1000 < beste.preis_je_1000))
    .sort((a, b) => b.preis_je_1000 - a.preis_je_1000)[0]
  let naechste: string | null = null
  if (offen) {
    const rest = schwelle(offen) - gezaehlt(offen)
    const fehlt = nachPaletten(offen) ? `${pal(rest)} Pal.` : `${stueck(rest)} Stück`
    naechste = `noch ${fehlt} bis ${staffelSchwelle(offen)} (${preis(offen.preis_je_1000)} € je 1.000)`
  }
  return { erreicht: beste ? `${staffelSchwelle(beste)}${beste.artikelgruppe ? '' : ' (Sortiment)'}` : null, naechste }
}

/**
 * Je Artikelgruppe Summe, Mindestabruf und Staffel. Liefert das Backend `gruppen`, gelten die; sonst zur Anzeige
 * aus den Positionen und dem Lieferanten gerechnet. Null, wenn die Variante keine Gruppen berührt - dann reicht
 * die Kopfzeile der Karte.
 */
function gruppenSummen(v: Variante, l: Lieferant | undefined): VarianteGruppe[] | null {
  if (v.gruppen && v.gruppen.length > 0) return v.gruppen
  if (!l) return null
  const gruppen = l.gruppen ?? []
  const zuordnung = new Map<string, string | null>()
  for (const a of l.artikel_details ?? []) zuordnung.set(a.artikelnummer, a.artikelgruppe)
  for (const g of gruppen) for (const n of g.artikel) if (!zuordnung.has(n)) zuordnung.set(n, g.name)
  const gruppeVon = (p: VorschlagPosition) =>
    p.artikelgruppe !== undefined ? p.artikelgruppe : (zuordnung.get(p.artikelnummer) ?? null)
  const namen = [...new Set(v.positionen.map(gruppeVon))]
  if (!namen.some((n) => n !== null)) return null
  namen.sort((a, b) => (a === b ? 0 : a === null ? 1 : b === null ? -1 : a.localeCompare(b, 'de')))
  const gesamt = {
    paletten: v.positionen.reduce((n, p) => n + p.paletten, 0),
    stueck: v.positionen.reduce((n, p) => n + p.stueck, 0),
  }
  return namen.map((name) => {
    const positionen = v.positionen.filter((p) => gruppeVon(p) === name)
    const paletten = positionen.reduce((n, p) => n + p.paletten, 0)
    const stueckSumme = positionen.reduce((n, p) => n + p.stueck, 0)
    const kosten = positionen.some((p) => p.kosten !== null) ? positionen.reduce((n, p) => n + (p.kosten ?? 0), 0) : null
    const mindest = gruppen.find((g) => g.name === name)?.mindestabruf_paletten ?? l.mindestabruf_paletten ?? null
    const preise = [...new Set(positionen.filter((p) => p.paletten > 0 && p.preis_je_1000 !== null).map((p) => p.preis_je_1000))]
    const stand = staffelStand(l.staffeln, name, { paletten, stueck: stueckSumme }, gesamt)
    return {
      artikelgruppe: name,
      paletten,
      stueck: stueckSumme,
      mindestabruf_paletten: mindest,
      mindestabruf_erfuellt: mindest === null ? null : paletten >= mindest,
      staffel: stand.erreicht,
      preis_je_1000: preise.length === 1 ? preise[0] : null,
      naechste_staffel: stand.naechste,
      kosten,
    }
  })
}

/** Je Artikelgruppe: Paletten, Mindestabruf erreicht oder nicht, Staffel und Preis, nächste Staffel, Kosten. */
function GruppenSummen({ summen, preiseZeigen }: { summen: VarianteGruppe[]; preiseZeigen: boolean }) {
  return (
    <div className="tabellenrahmen" style={{ marginBottom: 8 }}>
      <table>
        <thead>
          <tr>
            <th>Artikelgruppe</th>
            <th className="zahl">Paletten</th>
            <th className="zahl">Stück</th>
            <th>Menge</th>
            <th>Staffel</th>
            {preiseZeigen && <th className="zahl">€ je 1.000</th>}
            <th>nächste Staffel</th>
            {preiseZeigen && <th className="zahl">Kosten</th>}
          </tr>
        </thead>
        <tbody>
          {summen.map((g) => {
            const mindest = g.mindestabruf_paletten
            const erfuellt = g.mindestabruf_erfuellt ?? (mindest === null ? null : g.paletten >= mindest)
            return (
              <tr key={g.artikelgruppe ?? ''} className={erfuellt === false ? 'gruppe-unter-minimum' : ''}>
                <td>
                  <b>{g.artikelgruppe ?? 'ohne Gruppe'}</b>
                </td>
                <td className="zahl">{pal(g.paletten)}</td>
                <td className="zahl">{g.stueck !== null ? stueck(g.stueck) : ''}</td>
                <td className={erfuellt === false ? 'unter-mindestbestand' : ''}>
                  {/* Den Satz liefert das Backend für jede Regel; „genau 33“ oder „ein Vielfaches von 33“ (Berry) ist
                      mehr als eine Untergrenze - bei 41 Paletten stand hier „es fehlen -8“ (Review 04.11.2026). */}
                  {g.mengenregel_text ? (
                    `${g.mengenregel_text} - ${
                      erfuellt
                        ? 'erfüllt'
                        : g.mengenregel === 'mindestens' && mindest !== null
                          ? `es fehlen ${pal(Math.max(0, mindest - g.paletten))}`
                          : 'nicht erfüllt'
                    }`
                  ) : mindest === null ? (
                    <span className="hinweis">keiner</span>
                  ) : erfuellt ? (
                    `${mindest} Pal. - erreicht`
                  ) : (
                    `${mindest} Pal. - nicht erreicht, es fehlen ${pal(Math.max(0, mindest - g.paletten))}`
                  )}
                </td>
                <td>{g.staffel ?? <span className="hinweis">keine</span>}</td>
                {preiseZeigen && <td className="zahl">{preis(g.preis_je_1000)}</td>}
                <td className="hinweis">{g.naechste_staffel ?? ''}</td>
                {preiseZeigen && <td className="zahl">{euro(g.kosten)}</td>}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function VarianteKarte({
  variante: v,
  nummer,
  gewaehlt,
  entscheidbar,
  aufteilbar,
  gesperrt,
  preiseZeigen,
  lieferant,
  onWaehlen,
  onAufteilen,
  onFehler,
}: {
  variante: Variante
  nummer: number
  gewaehlt: boolean
  entscheidbar: boolean
  /** Entwurf: Paletten je Position änderbar */
  aufteilbar: boolean
  gesperrt: boolean
  preiseZeigen: boolean
  lieferant: Lieferant | undefined
  onWaehlen: () => void
  onAufteilen: (paletten: Record<string, number>) => void
  onFehler: (text: string) => void
}) {
  const nichtsZuBestellen = v.paletten_gesamt === 0 && v.stueck_gesamt === 0
  const [eingaben, setEingaben] = useState<Record<string, string>>(() => palettenTexte(v))
  // Nach dem Neurechnen kommt eine neue Variante - die Felder zeigen dann ihre Paletten.
  useEffect(() => setEingaben(palettenTexte(v)), [v])
  const geaendert = aufteilbar ? v.positionen.filter((p) => (eingaben[p.artikelnummer] ?? '').trim() !== String(p.paletten)) : []
  const summen = gruppenSummen(v, lieferant)
  const gruppeJePosition = new Map<string, string | null>()
  if (summen) {
    for (const a of lieferant?.artikel_details ?? []) gruppeJePosition.set(a.artikelnummer, a.artikelgruppe)
    for (const g of lieferant?.gruppen ?? []) for (const n of g.artikel) if (!gruppeJePosition.has(n)) gruppeJePosition.set(n, g.name)
  }

  function neuRechnen() {
    const paletten: Record<string, number> = {}
    for (const p of geaendert) {
      const text = (eingaben[p.artikelnummer] ?? '').trim()
      const wert = text === '' ? 0 : zahlLesen(text)
      if (wert === null || wert < 0 || !Number.isInteger(wert)) {
        onFehler(`${p.artikelnummer}: „${text}“ ist keine ganze Palettenzahl (0 = weglassen).`)
        return
      }
      paletten[p.artikelnummer] = wert
    }
    if (Object.keys(paletten).length > 0) onAufteilen(paletten)
  }

  return (
    <div className={`karte variante ${gewaehlt ? 'gewaehlt' : ''}`} style={{ marginTop: 12 }}>
      <div className="leiste">
        <h4>
          Variante {nummer}: {v.titel}
          {gewaehlt && <span className="status gebucht" style={{ marginLeft: 8 }}>gewählt</span>}
        </h4>
        <span className="hinweis">
          {v.paletten_gesamt} Paletten · {stueck(v.stueck_gesamt)} Stück
          {v.preis_je_1000 !== null
            ? ` · ${
                v.staffel_ab_paletten != null || v.staffel_ab_stueck != null
                  ? `Staffel ${staffelSchwelle({ ab_stueck: v.staffel_ab_stueck, ab_paletten: v.staffel_ab_paletten })}: `
                  : ''
              }${preis(v.preis_je_1000)} € je 1.000`
            : ''}
          {v.kosten_gesamt !== null ? ` · ${euro(v.kosten_gesamt)}` : ''}
          {v.reicht_frueheste ? ` · reicht bis ${datum(v.reicht_frueheste)}${v.reicht_spaeteste && v.reicht_spaeteste !== v.reicht_frueheste ? ` bis ${datum(v.reicht_spaeteste)}` : ''}` : ''}
        </span>
        {entscheidbar && !nichtsZuBestellen && (
          <button
            className="haupt nicht-drucken"
            disabled={geaendert.length > 0}
            title={geaendert.length > 0 ? 'Erst „Neu rechnen“ - sonst gilt die alte Aufteilung.' : undefined}
            onClick={onWaehlen}
          >
            Diese Variante bestellen
          </button>
        )}
        {nichtsZuBestellen && <span className="status">Nichts zu bestellen</span>}
      </div>
      {summen && <GruppenSummen summen={summen} preiseZeigen={preiseZeigen} />}
      <div className="tabellenrahmen">
        <table>
          <thead>
            <tr>
              <th>Artikel</th>
              <th>Bezeichnung</th>
              {summen && <th>Gruppe</th>}
              <th className="zahl">Paletten</th>
              <th className="zahl">Stück</th>
              <th className="zahl">t FP</th>
              <th>reicht vorher</th>
              <th>reicht nachher</th>
              {preiseZeigen && <th className="zahl">€ je 1.000</th>}
              {preiseZeigen && <th className="zahl">Kosten</th>}
              <th>Hinweis</th>
            </tr>
          </thead>
          <tbody>
            {v.positionen.map((p) => (
              <tr key={p.artikelnummer} className={p.menge_t > 0 ? '' : 'leise'}>
                <td>{p.artikelnummer}</td>
                <td>{p.bezeichnung}</td>
                {summen && (
                  <td className="hinweis">
                    {(p.artikelgruppe !== undefined ? p.artikelgruppe : gruppeJePosition.get(p.artikelnummer)) ?? 'ohne'}
                  </td>
                )}
                <td className="zahl">
                  {aufteilbar ? (
                    <input
                      type="text"
                      inputMode="numeric"
                      className={`paletten-feld${geaendert.includes(p) ? ' geaendert' : ''}`}
                      aria-label={`Paletten ${p.artikelnummer}`}
                      value={eingaben[p.artikelnummer] ?? ''}
                      disabled={gesperrt}
                      onChange={(e) => setEingaben((alt) => ({ ...alt, [p.artikelnummer]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') neuRechnen()
                      }}
                    />
                  ) : (
                    p.paletten || ''
                  )}
                </td>
                <td className="zahl">{p.stueck ? stueck(p.stueck) : ''}</td>
                <td className="zahl">{p.menge_t ? tonnen(p.menge_t) : ''}</td>
                <td>{p.reicht_vorher_kw ?? ''}</td>
                <td>{p.reicht_nachher_kw ?? ''}</td>
                {preiseZeigen && <td className="zahl">{preis(p.preis_je_1000)}</td>}
                {preiseZeigen && <td className="zahl">{euro(p.kosten)}</td>}
                <td className="hinweis">{p.hinweise.join(' ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {aufteilbar && (
        <div className="leiste nicht-drucken" style={{ marginTop: 8 }}>
          <button disabled={gesperrt || geaendert.length === 0} onClick={neuRechnen}>
            Neu rechnen
          </button>
          {geaendert.length > 0 && (
            <button className="leise" onClick={() => setEingaben(palettenTexte(v))}>
              Zurücksetzen
            </button>
          )}
          <span className="hinweis">
            {geaendert.length > 0
              ? `${geaendert.length} Position(en) geändert - erst neu rechnen, dann bestellen.`
              : 'Paletten je Artikel lassen sich ändern, z.B. 10 und 20 von 30 innerhalb einer Gruppe; 0 = weglassen.'}
          </span>
        </div>
      )}
      {v.naechste_staffel && <p className="hinweis">{v.naechste_staffel}</p>}
      {v.einwaende.map((e, i) => (
        <Meldung key={i} art="warnung">
          {e}
        </Meldung>
      ))}
    </div>
  )
}
